import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { PublicLayout } from '@/components/public/PublicLayout';
import { Seo } from '@/components/Seo';
import { BookingDialog } from '@/components/public/BookingDialog';
import { GuideReviews } from '@/components/public/GuideReviews';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { parcoService } from '@/services/parco.service';
import {
  Languages, Sparkles, Mountain, Ruler, Clock, TrendingUp, CalendarRange,
  MapPin, ArrowLeft, Ticket, Compass, AlertCircle, ShieldCheck, Star,
} from 'lucide-react';

const initials = (name = '') =>
  name.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') || 'G';

function ItineraryCard({ area, onBook }) {
  return (
    <Card className="group flex flex-col overflow-hidden" data-testid={`guide-itinerary-${area.id}`}>
      <div className="relative h-44 overflow-hidden bg-muted">
        {area.cover_image ? (
          <img src={area.cover_image} alt={area.name} loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground"><Compass className="h-8 w-8" /></div>
        )}
        {area.price != null && (
          <span className="absolute right-3 top-3 rounded-full bg-background/90 px-3 py-1 text-sm font-semibold text-foreground shadow-sm backdrop-blur">€{area.price}</span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-4">
        <h3 className="font-display text-lg font-semibold leading-tight text-foreground">{area.name}</h3>
        {area.subtitle ? <p className="mt-0.5 line-clamp-1 text-sm text-muted-foreground">{area.subtitle}</p> : null}

        <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {area.difficulty ? <span className="inline-flex items-center gap-1 capitalize"><Mountain className="h-3.5 w-3.5" />{area.difficulty}</span> : null}
          {area.length_km != null ? <span className="inline-flex items-center gap-1"><Ruler className="h-3.5 w-3.5" />{area.length_km} km</span> : null}
          {area.elevation_gain_m != null ? <span className="inline-flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5" />{area.elevation_gain_m} m</span> : null}
          {area.duration ? <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{area.duration}</span> : null}
          {area.season ? <span className="inline-flex items-center gap-1"><CalendarRange className="h-3.5 w-3.5" />{area.season}</span> : null}
        </div>

        {(area.themes && area.themes.length > 0) && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {area.themes.slice(0, 4).map((t, i) => <Badge key={i} variant="secondary" className="text-[11px]">{t}</Badge>)}
          </div>
        )}

        <div className="mt-auto flex items-center justify-between pt-4">
          {area.points?.length ? (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="h-3.5 w-3.5" />{area.points.length} tappe</span>
          ) : <span />}
          <Button size="sm" onClick={() => onBook(area)} data-testid={`guide-book-${area.id}`}>
            <Ticket className="mr-2 h-4 w-4" />Prenota
          </Button>
        </div>
      </div>
    </Card>
  );
}

export default function GuidePage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [booking, setBooking] = useState({ open: false, area: null });

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try { const data = await parcoService.publicGuide(id); setData(data); }
    catch (e) { setError(true); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const openBooking = (area) => setBooking({ open: true, area });

  const guide = data?.guide;
  const areas = data?.areas || [];

  return (
    <PublicLayout>
      <Seo title={guide ? `${guide.display_name} · Guida del Parco` : 'Guida del Parco'}
        description={guide?.presentation || guide?.bio || 'Scopri la guida e i suoi itinerari nel Parco Nazionale della Sila.'}
        path={`/guida/${id}`} image={guide?.photo} />

      {/* HERO GUIDA */}
      <section className="relative overflow-hidden border-b border-border bg-gradient-to-b from-primary/10 to-background" data-testid="guide-hero">
        <div className="sila-section py-10 sm:py-14">
          <Link to="/il-parco-della-sila" className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground" data-testid="guide-back-link">
            <ArrowLeft className="h-4 w-4" />Torna al Parco
          </Link>

          {loading ? (
            <div className="flex flex-col items-start gap-5 sm:flex-row sm:items-center">
              <Skeleton className="h-28 w-28 rounded-full" />
              <div className="space-y-3">
                <Skeleton className="h-8 w-64" />
                <Skeleton className="h-4 w-80" />
                <Skeleton className="h-4 w-48" />
              </div>
            </div>
          ) : error || !guide ? (
            <div className="flex flex-col items-center gap-3 py-10 text-center" data-testid="guide-not-found">
              <AlertCircle className="h-10 w-10 text-muted-foreground" />
              <h1 className="font-display text-2xl font-semibold">Guida non trovata</h1>
              <p className="max-w-md text-muted-foreground">Questa guida non è disponibile o il suo profilo non è ancora stato pubblicato.</p>
              <Button asChild className="mt-2"><Link to="/il-parco-della-sila">Esplora il Parco</Link></Button>
            </div>
          ) : (
            <div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center">
              <Avatar className="h-28 w-28 border-2 border-background shadow-lg ring-1 ring-border" data-testid="guide-avatar">
                <AvatarImage src={guide.photo} alt={guide.display_name} />
                <AvatarFallback className="text-3xl">{initials(guide.display_name)}</AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary">
                    <Compass className="h-3.5 w-3.5" />Guida del Parco
                  </span>
                  {guide.verified ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary" data-testid="guide-verified-badge" title="Guida verificata AIGAE">
                      <ShieldCheck className="h-3.5 w-3.5" />Verificata AIGAE
                    </span>
                  ) : null}
                </div>
                <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-foreground sm:text-4xl" data-testid="guide-name">{guide.display_name}</h1>
                {guide.rating?.count > 0 && (
                  <div className="mt-2 flex items-center gap-2" data-testid="guide-hero-rating">
                    <span className="inline-flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Star key={i} className={`h-4 w-4 ${i <= Math.round(guide.rating.average) ? 'fill-amber-400 text-amber-400' : 'fill-none text-muted-foreground/40'}`} />
                      ))}
                    </span>
                    <span className="text-sm font-medium text-foreground">{guide.rating.average.toFixed(1)}</span>
                    <span className="text-sm text-muted-foreground">({guide.rating.count})</span>
                  </div>
                )}
                {guide.presentation ? <p className="mt-1.5 max-w-2xl text-lg text-muted-foreground">{guide.presentation}</p> : null}
                <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2">
                  {(guide.languages || []).length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Languages className="h-4 w-4 text-primary" />
                      {guide.languages.map((l, i) => <Badge key={i} variant="outline" className="text-xs">{l}</Badge>)}
                    </div>
                  )}
                  {(guide.specialties || []).length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Sparkles className="h-4 w-4 text-primary" />
                      {guide.specialties.map((s, i) => <Badge key={i} variant="secondary" className="text-xs">{s}</Badge>)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* BIO + ITINERARI */}
      {!loading && !error && guide && (
        <section className="sila-section py-10 sm:py-14">
          {guide.bio ? (
            <div className="mb-10 max-w-3xl" data-testid="guide-bio">
              <h2 className="mb-3 font-display text-xl font-semibold text-foreground">Chi sono</h2>
              <p className="whitespace-pre-line text-[1.02rem] leading-relaxed text-muted-foreground">{guide.bio}</p>
            </div>
          ) : null}

          <div className="mb-5 flex items-center gap-2">
            <Compass className="h-5 w-5 text-primary" />
            <h2 className="font-display text-xl font-semibold text-foreground">I miei itinerari</h2>
            <Badge variant="secondary" className="ml-1">{areas.length}</Badge>
          </div>

          {areas.length === 0 ? (
            <Card className="flex flex-col items-center justify-center gap-2 p-12 text-center text-muted-foreground" data-testid="guide-no-itineraries">
              <MapPin className="h-8 w-8" />
              <p>Questa guida non ha ancora itinerari pubblicati.</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {areas.map((a) => <ItineraryCard key={a.id} area={a} onBook={openBooking} />)}
            </div>
          )}
        </section>
      )}

      {!loading && !error && guide && <GuideReviews guideId={id} />}

      <BookingDialog open={booking.open} onOpenChange={(o) => setBooking((b) => ({ ...b, open: o }))} area={booking.area} />
    </PublicLayout>
  );
}
